import {
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material'
import { ModelListRowModel } from '../../types'
import { ItemTile } from '~/ui/components/ItemTile'
import {
  IconAlertTriangle,
  IconArrowRightCircleFilled,
  IconArrowsSort,
  IconBuildingStore,
  IconCoin,
  IconDotsVertical,
  IconExternalLink,
  IconInfoCircle,
  IconRefresh,
  IconTag,
  IconTrash,
} from '@tabler/icons-react'
import { OverviewCard } from '~/ui/components/OverviewCard'
import { useMemo, useState } from 'react'
import * as R from 'remeda'
import { entries, groupBy, mapToObj, sortBy } from 'remeda'
import { CopyToClipboardButton } from '~/ui/components/CopyToClipboardButton'
import { InventoryStatus } from '~/supabase/types'
import { Link } from 'react-router'
import { useUpdateInventory } from '~/features/visitors/hooks/useUpdateInventory'
import { useQueryClient } from '@tanstack/react-query'
import { GET_INVENTORY_QUERY_KEY } from '~/features/hooks/useGetInventory'
import { toast } from 'sonner'
import { useSetShopifyQuantity } from '~/features/shopify/hooks/useSetShopifyQuantity'

import { createShopifyVariantUrl, SHOPIFY_LOCATION_ID } from '~/features/shopify/utils'

type SortConfig = {
  type: InventoryStatus
  direction: 'asc' | 'desc'
}

export const InventoryDetails = ({ data }: { data: ModelListRowModel }) => {
  const queryClient = useQueryClient()
  const { mutate: updateInventory, isPending: isUpdatingInventory } = useUpdateInventory()
  const { mutate: setShopifyQuantity, isPending: isSettingQuantity } = useSetShopifyQuantity()

  const [anchorEl, setAnchorEl] = useState<{
    element: HTMLButtonElement
    variantId?: string
    inventoryItemId?: string
    size: string
    inventories: typeof data.inventory
    hasInconsistency: boolean
  } | null>(null)

  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLDivElement | null>(null)
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null)

  const shopifyConfigMap = useMemo(() => {
    return {
      inventoryItems: mapToObj(data.shopifyProduct?.variants.nodes ?? [], (variant) => [
        variant.title,
        variant.inventoryItem.id,
      ]),
      variants: mapToObj(data.shopifyProduct?.variants.nodes ?? [], (variant) => [
        variant.title,
        variant.id,
      ]),
    }
  }, [data.shopifyProduct?.variants.nodes])

  const sortedInventoryEntries = useMemo(() => {
    const groupedInventory = groupBy(data.inventory, (d) => d.size)
    return sortBy(entries(groupedInventory), [
      ([size, values]) =>
        sortConfig
          ? sortConfig.type === 'available'
            ? values.filter((i) => i.status === 'available').length
            : values.filter((i) => i.status === 'sold').length
          : size,
      sortConfig ? sortConfig.direction : 'desc',
    ])
  }, [data.inventory, sortConfig])

  const alertMessage = useMemo(() => {
    if (!data.shopifyProduct) return null

    const filteredInventory = R.pipe(
      data.inventory,
      R.map((inv) => inv.product_variant_id),
      R.filter(R.isNonNull)
    )
    const difference = R.difference(
      filteredInventory,
      data.inventory.map((d) => d.product_variant_id)
    )
    if (difference.length !== 0)
      return `The following variants are missing in ids in database: ${difference.join(', ')}`

    const shopifyProductMap = mapToObj(data.shopifyProduct?.variants.nodes ?? [], (variant) => [
      variant.id,
      true,
    ])
    const missingVariants = filteredInventory.filter((variantId) => !shopifyProductMap[variantId])
    if (missingVariants.length !== 0)
      return `The following variants are missing in shopify: ${missingVariants.join(', ')}`
    return null
  }, [data.inventory, data.shopifyProduct])

  const handleSetSortConfig = (config: SortConfig) => {
    setSortConfig(config)
    setSortAnchorEl(null)
  }

  const handleFixShopifyIds = () => {
    if (!anchorEl) return

    const { variantId, inventoryItemId, inventories } = anchorEl

    if (!variantId || !inventoryItemId) {
      toast.error('Missing Shopify IDs for this size')
      return
    }

    // Close menu immediately to prevent anchor element issues during re-render
    setAnchorEl(null)

    // Update all inventory entries for this size with the correct IDs
    const updates = inventories.map((inv) => ({
      id: inv.id,
      product_variant_id: variantId,
      inventory_item_id: inventoryItemId,
    }))

    updateInventory(updates, {
      onSuccess: () => {
        toast.success('Shopify IDs updated successfully')
        queryClient.invalidateQueries({ queryKey: [GET_INVENTORY_QUERY_KEY] })
      },
      onError: () => {
        toast.error('Failed to update Shopify IDs')
      },
    })
  }

  const handleWriteOff = () => {
    if (!anchorEl) return

    const { inventoryItemId, inventories, size } = anchorEl

    // Find one available inventory item to write off
    const availableInventory = inventories.find((inv) => inv.status === 'available')

    if (!availableInventory) {
      toast.error(`No available inventory to write off for size ${size}`)
      return
    }

    // Get current quantity
    const currentQuantity = inventories.filter((inv) => inv.status === 'available').length

    // Close menu immediately
    setAnchorEl(null)

    // Update inventory status to unavailable
    updateInventory(
      [
        {
          id: availableInventory.id,
          status: 'unavailable',
        },
      ],
      {
        onSuccess: () => {
          if (!inventoryItemId) {
            return
          }
          // Update Shopify quantity
          setShopifyQuantity(
            {
              name: 'available',
              reason: 'correction',
              quantities: [
                {
                  inventoryItemId,
                  locationId: SHOPIFY_LOCATION_ID,
                  quantity: currentQuantity - 1,
                  compareQuantity: currentQuantity,
                },
              ],
            },
            {
              onSuccess: () => {
                toast.success(`Item written off for size ${size}`)
                queryClient.invalidateQueries({ queryKey: [GET_INVENTORY_QUERY_KEY] })
              },
              onError: () => {
                toast.error('Failed to update Shopify quantity')
                // Rollback the inventory status change
                updateInventory([
                  {
                    id: availableInventory.id,
                    status: 'available',
                  },
                ])
              },
            }
          )
        },
        onError: () => {
          toast.error('Failed to write off item')
        },
      }
    )
  }

  return (
    <Stack sx={{ overflow: 'hidden', height: 1 }} spacing={1} useFlexGap>
      <Stack direction="row" spacing={1} useFlexGap>
        <OverviewCard
          Icon={IconInfoCircle}
          value={<Typography variant="subtitle1">{data.stock_code}</Typography>}
          description="Stock code"
          color="secondary"
          variant="outlined"
        />
        <OverviewCard
          Icon={IconCoin}
          value={<Typography variant="subtitle1">{data.soldInventory}</Typography>}
          description="Sold"
          color="success"
          variant="outlined"
        />
        <OverviewCard
          Icon={IconBuildingStore}
          value={<Typography variant="subtitle1">{data.availableInventory}</Typography>}
          description="Available"
          color="info"
          variant="outlined"
        />
      </Stack>
      {alertMessage ? (
        <Alert
          severity="warning"
          variant="outlined"
          icon={
            <SvgIcon fontSize="small">
              <IconAlertTriangle />
            </SvgIcon>
          }
        >
          <AlertTitle>Warning</AlertTitle>
          <Typography variant="body2">{alertMessage}</Typography>
        </Alert>
      ) : null}
      <Stack>
        <Chip
          label={
            <Typography variant="body2" sx={{ span: { fontWeight: 'bold' } }}>
              Sort{' '}
              {sortConfig ? (
                <>
                  <span>{sortConfig.type === 'available' ? 'available' : 'sold'}</span> by{' '}
                  <span>{sortConfig.direction === 'asc' ? 'asc' : 'desc'}</span>{' '}
                </>
              ) : (
                'size by desc'
              )}
            </Typography>
          }
          sx={{ alignSelf: 'flex-end' }}
          size="small"
          color="info"
          icon={
            <SvgIcon fontSize="small">
              <IconArrowsSort />
            </SvgIcon>
          }
          onDelete={sortConfig ? () => setSortConfig(null) : undefined}
          onClick={(e) => setSortAnchorEl(e.currentTarget)}
        />
        <Menu anchorEl={sortAnchorEl} open={!!sortAnchorEl} onClose={() => setSortAnchorEl(null)}>
          <MenuItem onClick={() => handleSetSortConfig({ type: 'available', direction: 'asc' })}>
            <Typography variant="body2">Sort by available (asc)</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleSetSortConfig({ type: 'available', direction: 'desc' })}>
            <Typography variant="body2">Sort by available (desc)</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleSetSortConfig({ type: 'sold', direction: 'asc' })}>
            <Typography variant="body2">Sort by sold (asc)</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleSetSortConfig({ type: 'sold', direction: 'desc' })}>
            <Typography variant="body2">Sort by sold (desc)</Typography>
          </MenuItem>
        </Menu>
      </Stack>
      <Stack sx={{ overflow: 'hidden', flexGrow: 1 }}>
        <Menu anchorEl={anchorEl?.element} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem
            component={Link}
            to={
              data.shopify_product_id && anchorEl?.variantId
                ? createShopifyVariantUrl(data.shopify_product_id, anchorEl?.variantId)
                : ''
            }
            target="_blank"
            disabled={!anchorEl?.variantId || !data.shopify_product_id}
          >
            <SvgIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }}>
              <IconExternalLink />
            </SvgIcon>
            <Typography variant="body2">Go to shopify</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleFixShopifyIds}
            disabled={
              !anchorEl?.variantId ||
              !anchorEl?.inventoryItemId ||
              isUpdatingInventory ||
              !anchorEl?.hasInconsistency
            }
          >
            <SvgIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }}>
              <IconRefresh />
            </SvgIcon>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Fix Shopify IDs
              {isUpdatingInventory && <CircularProgress size={16} />}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={handleWriteOff}
            disabled={
              isUpdatingInventory ||
              isSettingQuantity ||
              !anchorEl?.inventories.some((inv) => inv.status === 'available')
            }
          >
            <SvgIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }}>
              <IconTrash />
            </SvgIcon>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Write off
              {(isUpdatingInventory || isSettingQuantity) && <CircularProgress size={16} />}
            </Typography>
          </MenuItem>
          <MenuItem disabled={!anchorEl?.inventoryItemId}>
            <CopyToClipboardButton
              label={anchorEl?.inventoryItemId ? 'Copy inv. id' : 'Inv. id missing'}
              content={anchorEl?.inventoryItemId ?? 'missing'}
              disabled={!anchorEl?.inventoryItemId}
              size="small"
              onCopy={() => setAnchorEl(null)}
            />
          </MenuItem>
          <MenuItem disabled={!anchorEl?.variantId}>
            <CopyToClipboardButton
              label={anchorEl?.variantId ? 'Copy var. id' : 'Var. id missing'}
              content={anchorEl?.variantId ?? 'missing'}
              disabled={!anchorEl?.variantId}
              size="small"
              onCopy={() => setAnchorEl(null)}
            />
          </MenuItem>
        </Menu>
        <Stack spacing={1} useFlexGap sx={{ overflow: 'auto' }}>
          {sortedInventoryEntries.map(([size, inventories]) => {
            const inventoryItemId = shopifyConfigMap.inventoryItems[size]
            const variantId = shopifyConfigMap.variants[size]
            const availableInventory = inventories.filter((i) => i.status === 'available')
            const inconsistentInventoryId = availableInventory.some(
              (inv) => inv.inventory_item_id !== inventoryItemId
            )
            const inconsistentVariantId = availableInventory.some(
              (inv) => inv.product_variant_id !== variantId
            )
            const hasInconsistency = inconsistentInventoryId || inconsistentVariantId
            return (
              <ItemTile
                Icon={IconTag}
                key={size}
                primaryButton={
                  <IconButton
                    onClick={(e) =>
                      setAnchorEl({
                        element: e.currentTarget,
                        variantId,
                        inventoryItemId,
                        size: size.toString(),
                        inventories,
                        hasInconsistency,
                      })
                    }
                  >
                    <IconDotsVertical />
                  </IconButton>
                }
                showPrimaryButton={!!anchorEl}
              >
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', flexGrow: 1 }}
                  spacing={1}
                  useFlexGap
                >
                  <Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      divider={
                        <SvgIcon fontSize="small" sx={{ color: 'text.secondary' }}>
                          <IconArrowRightCircleFilled />
                        </SvgIcon>
                      }
                      sx={{ alignItems: 'center' }}
                    >
                      <Typography variant="subtitle2">{size}</Typography>
                      {inconsistentInventoryId ? (
                        <Typography variant="caption" color="warning">
                          Inv. id inconsistent
                        </Typography>
                      ) : inconsistentVariantId ? (
                        <Typography variant="caption" color="warning">
                          Var. id inconsistent
                        </Typography>
                      ) : null}
                    </Stack>
                    <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1} useFlexGap>
                      <Typography variant="caption" color="text.secondary">
                        available: {inventories.filter((i) => i.status === 'available').length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        sold: {inventories.filter((i) => i.status === 'sold').length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        item id: {inventoryItemId?.split('/').pop()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        var id: {variantId?.split('/').pop()}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </ItemTile>
            )
          })}
        </Stack>
      </Stack>
    </Stack>
  )
}
